import { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { useOktaAuth } from '@okta/okta-react';
import { useProductSearch } from '../hooks/useProductSearch';
import { SearchParams } from '../hooks/queryKeys';
import { Box, Stack, Typography, Alert } from '@mui/material';
import {
  DataGrid,
  GridColDef,
  GridFilterModel,
  GridFooter,
  GridFooterContainer,
  GridToolbar,
  useGridApiRef,
} from '@mui/x-data-grid';
import Indexes from '../enum/Indexes';
import Signs from './indexColumnDef/Signs';
import PipeMarking from './indexColumnDef/PipeMarking';
import Tags from './indexColumnDef/Tags';
import AreaMarking from './indexColumnDef/AreaMarking';
import Labels from './indexColumnDef/Labels';
import IndexBase from './indexColumnDef/IndexBase';
import SyncedScrollBar from './SyncedScrollBar';
import SearchFeedback from './searchFeedback';
import { useSearch } from '../context/SearchContext';
import { toast } from 'react-toastify';
import ProductTypes from '../enum/ProductTypesEnum';
import BenchtopSystemsOther from './indexColumnDef/BenchtopSystemsOther';
import Rfid from './indexColumnDef/Rfid';
import Consumable from './indexColumnDef/Consumable';
import Printers from './indexColumnDef/Printers';
import GeneralSafety from './indexColumnDef/GeneralSafety';
import LockoutTagout from './indexColumnDef/LockoutTagout';
import Absorbents from './indexColumnDef/Absorbents';
import ISalesOrg from '../models/iSalesOrg';

interface IProductFinderResultsProps {
  correlationId: string | null;
  onSearchComplete: () => void;
}

export const ProductFinderResults = (props: IProductFinderResultsProps) => {
  const { authState } = useOktaAuth();
  const [filterModel, setFilterModel] = useState<GridFilterModel>();
  const [columns, setColumns] = useState<GridColDef[]>([]);
  const [searchId, setSearchId] = useState<string | null>(null);
  const apiRef = useGridApiRef();
  const { state: SearchState } = useSearch();
  const { filters, selectedIndexes, allIndexes, selectedSalesOrgs, enabledProductTypes } = SearchState;

  const correlationIdRef = useRef<string | null>(null);

  const { correlationId, onSearchComplete } = props;

  const onSearchCompleteRef = useRef<() => void>(onSearchComplete);

  // Generate search parameters
  const immediateSearchParams = useMemo((): SearchParams | null => {
    const completedFilters = filters.filter(
      (filter) => filter.attribute && filter.selectedValue && filter.selectedValue.trim() !== ''
    );

    const token = authState?.accessToken?.accessToken || '';
    const effectiveIndexes = selectedIndexes.length > 0 ? selectedIndexes : allIndexes;

    return {
      bearerToken: token,
      filters: completedFilters,
      indexNames: effectiveIndexes,
      options: {
        salesOrgDivs: selectedSalesOrgs.map((org: ISalesOrg) => org.salesOrgId) ?? [],
        enabledProductTypes,
        correlationId: correlationId || correlationIdRef.current,
      },
    };
  }, [
    filters,
    selectedIndexes,
    selectedSalesOrgs,
    enabledProductTypes,
    correlationId,
    authState?.accessToken?.accessToken,
    allIndexes,
  ]);

  // Debounced search parameters (500ms delay)
  const [debouncedSearchParams, setDebouncedSearchParams] = useState<SearchParams | null>(immediateSearchParams);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchParams(immediateSearchParams);
    }, 500);

    return () => clearTimeout(timer);
  }, [immediateSearchParams]);

  const {
    data: searchResult,
    isLoading,
    error,
  } = useProductSearch(
    debouncedSearchParams || { bearerToken: '', filters: [], indexNames: [], options: {} },
    !!debouncedSearchParams
  );

  // Handle search errors with toast notification
  useEffect(() => {
    if (error) {
      toast.error('Error fetching products. Please try again.');
    }
  }, [error]);

  // Extract products and hasMoreResults from the search result
  const products = searchResult?.products ?? [];
  const hasMoreResults = searchResult?.hasMoreResults || false;

  correlationIdRef.current = correlationId;
  onSearchCompleteRef.current = onSearchComplete;

  useEffect(() => {
    // When multiple indexes selected or no specific index, always use IndexBase columns
    if (selectedIndexes.length === 1) {
      // Single index - use specific columns if available
      const singleIndex = selectedIndexes[0];
      switch (singleIndex) {
        case Indexes.Signs:
          setColumns(Signs);
          break;
        case Indexes.PipeMarking:
          setColumns(PipeMarking);
          break;
        case Indexes.Tags:
          setColumns(Tags);
          break;
        case Indexes.AreaMarking:
          setColumns(AreaMarking);
          break;
        case Indexes.Labels:
          setColumns(Labels);
          break;
        case Indexes.BenchtopSystemsOther:
          setColumns(BenchtopSystemsOther);
          break;
        case Indexes.Rfid:
          setColumns(Rfid);
          break;
        case Indexes.Consumables:
          setColumns(Consumable);
          break;
        case Indexes.Printers:
          setColumns(Printers);
          break;
        case Indexes.Absorbents:
          setColumns(Absorbents);
          break;
        case Indexes.GeneralSafety:
          setColumns(GeneralSafety);
          break;
        case Indexes.LockoutTagout:
          setColumns(LockoutTagout);
          break;
        default:
          setColumns(IndexBase);
          break;
      }
    } else {
      setColumns(IndexBase); // Always includes index column
    }
  }, [selectedIndexes]);

  // Handle search completion callback
  const handleSearchComplete = useCallback((newSearchId: string) => {
    setSearchId(newSearchId); // Store the new searchId for feedback
    onSearchCompleteRef.current();
  }, []);

  useEffect(() => {
    if (searchResult?.correlationId) {
      handleSearchComplete(searchResult.correlationId);
    }
  }, [searchResult?.correlationId, handleSearchComplete]);

  const ResultsHeader = () => {
    return (
      <Stack spacing={0}>
        <GridFooterContainer
          sx={{
            position: 'relative',
            top: 0,
            borderBottom: '1px solid lightgray',
            justifyContent: 'flex-end',
            width: '100%',
          }}
        >
          <GridFooter />
        </GridFooterContainer>
        <GridToolbar showQuickFilter sx={{ width: '100%' }} />
        <SyncedScrollBar apiRef={apiRef} columns={columns} products={products} />
      </Stack>
    );
  };

  return (
    <Box>
      <Box display={'flex'} justifyContent={'space-between'} alignItems={'center'}>
        <Typography variant="h5" component="h2" gutterBottom fontWeight="bold" sx={{ mt: 4, mb: 2 }}>
          Results
        </Typography>
        {searchId && <SearchFeedback searchId={searchId} />}
      </Box>
      {/* Show truncation warning when results reach the maximum limit */}
      {hasMoreResults && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Results have been limited to {products.length} items. Please refine your search criteria to see more specific
          results.
        </Alert>
      )}
      {/* Container with a fixed height rather then autoheight, only way to freeze column headers */}
      <Box sx={{ height: '100vh' }}>
        <DataGrid
          getRowId={(row) => {
            const rawId =
              row.type === ProductTypes.Eto
                ? `${row.id}-${row.salesOrgDiv || ''}-${row.productGroup || ''}`
                : `${row.id}-${row.productGroup || ''}`;

            return rawId.replaceAll(' ', ''); // Product Group has spaces which can cause issues as a key
          }}
          sx={{
            //Fixed height so that the loading and no result overlays are visible without scrolling
            '& .MuiDataGrid-overlayWrapperInner': { height: '100px !important' },

            //Increased width so it is more obvious that there is a specific table scrollbar
            '& .MuiDataGrid-scrollbar.MuiDataGrid-scrollbar--vertical::-webkit-scrollbar': { width: '15px' },
          }}
          apiRef={apiRef}
          slots={{ toolbar: ResultsHeader }}
          slotProps={{
            pagination: {
              showFirstButton: true,
              showLastButton: true,
            },
          }}
          filterModel={filterModel}
          onFilterModelChange={(newFilterModel) => setFilterModel(newFilterModel)}
          rows={products}
          columns={columns}
          loading={isLoading}
          disableColumnFilter={true}
          initialState={{
            pagination: {
              paginationModel: {
                pageSize: 25,
              },
            },
          }}
          pageSizeOptions={[5, 10, 25, 50]}
        />
      </Box>
    </Box>
  );
};
